import awsRegionsList from "aws-regions/regions.json";
import jwt_decode from "jwt-decode";
import { APPROACHING_COMPLETION_DAYS, AUDIT_STATUSES } from "./constants";

/** Signed-in user's email, lowercased; empty when no session is stored. */
export const readCurrentUserEmail = () => {
  try {
    const raw = localStorage.getItem("authUser");
    return raw ? String(jwt_decode(JSON.parse(raw).idToken)?.email || "").trim().toLowerCase() : "";
  } catch (_error) {
    return "";
  }
};

/** Completed or cancelled assessments may be deleted only by their owner. */
export const canDeleteClosedAudit = (audit, currentUserEmail) =>
  Boolean(currentUserEmail) &&
  (audit?.status === AUDIT_STATUSES.COMPLETED || audit?.status === AUDIT_STATUSES.CANCELLED) &&
  String(audit?.ownerEmail || "").trim().toLowerCase() === currentUserEmail;

/** Commercial regions the assessment scan can target. GovCloud is not offered. */
export const AWS_REGION_CODES = (Array.isArray(awsRegionsList) ? awsRegionsList : [])
  .map((region) => region?.code)
  .filter((code) => typeof code === "string" && code && !code.includes("-gov-"));

export const formatUserName = (user) => {
  if (!user) return "";
  const parts = [user.firstname, user.lastName].filter(
    (part) => part && part !== "null" && part !== "undefined"
  );
  if (parts.length) return parts.join(" ");
  return user.username || user.email || "";
};

export const formatUserLabel = (user) => {
  const name = formatUserName(user);
  if (name && user?.email && name !== user.email) return `${name} (${user.email})`;
  return user?.email || name || "";
};

/** Account name with its number, or the number alone when there is no distinct name. */
export const scopeLabel = (target) => {
  if (!target) return "";
  const name = target.accountName && target.accountName !== target.accountNumber ? target.accountName : "";
  return name ? `${name} (${target.accountNumber})` : target.accountNumber || target.auditScopeId || "";
};

export const getInitials = (name = "") => {
  const bits = String(name).trim().split(/\s+/).filter(Boolean);
  if (!bits.length) return "?";
  if (bits.length === 1) return bits[0].slice(0, 1).toUpperCase();
  return `${bits[0].slice(0, 1)}${bits[bits.length - 1].slice(0, 1)}`.toUpperCase();
};

const utcDateParts = (isoDate) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate || ""));
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]) - 1, day: Number(match[3]) };
};

/** Whole UTC days from today until the API date. The clock is not shifted into the browser zone. */
export const daysUntil = (isoDate) => {
  const parts = utcDateParts(isoDate);
  if (!parts) return null;
  const target = Date.UTC(parts.year, parts.month, parts.day);
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((target - today) / 86400000);
};

/** Calendar date from the UTC `YYYY-MM-DD` the API sent. */
export const formatShortDate = (isoDate) => {
  const parts = utcDateParts(isoDate);
  if (!parts) return "—";
  const date = new Date(Date.UTC(parts.year, parts.month, parts.day));
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;
const HAS_TIME = /[T ]\d{2}:\d{2}/;

/**
 * API instants are UTC. A value with no zone is treated as UTC.
 * A date-only `YYYY-MM-DD` is not an instant and is left unparsed.
 */
const parseUtcInstant = (value) => {
  if (value == null || value === "") return null;
  const raw = String(value).trim();
  if (!raw || !HAS_TIME.test(raw)) return null;
  const normalized = HAS_ZONE.test(raw) ? raw.replace(" ", "T") : `${raw.replace(" ", "T")}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * The same UTC instant, shown in the browser's timezone.
 * The short zone name (EDT, PST, …) comes from that zone, so each user sees their own clock.
 */
export const formatDateTime = (value) => {
  const date = parseUtcInstant(value);
  if (!date) return "";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZoneName: "short",
  });
};

export const isActiveStatus = (status) => status === AUDIT_STATUSES.ACTIVE;

export const isStaleAudit = (audit) => {
  if (!isActiveStatus(audit?.status)) return false;
  if (typeof audit.isStale === "boolean") return audit.isStale;
  return false;
};

export const isApproachingCompletion = (audit) => {
  if (!isActiveStatus(audit?.status)) return false;
  const remaining = daysUntil(audit.dueDate);
  return remaining != null && remaining >= 0 && remaining <= APPROACHING_COMPLETION_DAYS;
};

export const formatDaysRemaining = (audit) => {
  if (!isActiveStatus(audit?.status)) return "—";
  const remaining = daysUntil(audit.dueDate);
  if (remaining == null) return "—";
  if (remaining < 0) return `${Math.abs(remaining)}d overdue`;
  if (remaining === 0) return "Due today";
  if (remaining === 1) return "1 day";
  return `${remaining} days`;
};

export const daysRemainingTone = (audit) => {
  if (!isActiveStatus(audit?.status)) return "default";
  const remaining = daysUntil(audit.dueDate);
  if (remaining == null) return "default";
  if (remaining < 0) return "overdue";
  if (remaining <= APPROACHING_COMPLETION_DAYS) return "warn";
  return "default";
};

/**
 * Audit scores are Σpassed / Σ(passed + failed): a ratio in [0, 1], null when nothing is scoreable.
 * Values above 1 are already on a 0–100 scale.
 */
export const scorePercent = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  if (n <= 1) return Math.max(0, n * 100);
  return Math.min(100, n);
};

export const frameworkLabel = (framework) =>
  framework?.name || framework?.complianceKey || (framework?.complianceAddonId != null
    ? `Framework ${framework.complianceAddonId}`
    : "—");

export const frameworkKey = (framework) =>
  String(framework?.complianceAddonId ?? framework?.complianceKey ?? framework?.name ?? "");

const emailDomainOf = (email) => {
  if (!email || typeof email !== "string") return null;
  const at = email.lastIndexOf("@");
  if (at < 1 || at === email.length - 1) return null;
  return email.slice(at + 1).toLowerCase();
};

/**
 * Owner and default assignee are client-organization users.
 * When the scoped org is a customer under an MSP, the shared users list also
 * contains MSP users. Those are dropped by matching the customer org primary
 * email domain — the same rule Configuration already uses. There is no Audit
 * Hub users route. When the scoped org is not a customer, the list is already
 * that organization's users.
 */
const customerOrgDomain = (organization) => {
  let customer = false;
  try {
    const raw = localStorage.getItem("selectedOrganization");
    if (raw) customer = JSON.parse(raw)?.customer === true;
  } catch (_e) {
    customer = false;
  }
  return customer ? emailDomainOf(organization?.orgprimaryemail) : null;
};

export const excludeMspUsers = (users, organization) => {
  const list = Array.isArray(users) ? users : [];
  const orgDomain = customerOrgDomain(organization);
  if (!orgDomain) return list;
  return list.filter((user) => emailDomainOf(user?.email) === orgDomain);
};

/**
 * Auditors / reviewers. Deciding a control needs an organization above the
 * scoped one, so on a customer org these are the MSP users that
 * `excludeMspUsers` drops. Otherwise the list is returned unchanged.
 */
export const mspUsersOnly = (users, organization) => {
  const list = Array.isArray(users) ? users : [];
  const orgDomain = customerOrgDomain(organization);
  if (!orgDomain) return list;
  return list.filter((user) => {
    const domain = emailDomainOf(user?.email);
    return Boolean(domain) && domain !== orgDomain;
  });
};

/**
 * Auditor / reviewer candidates. Under a primary (MSP) organization they are that
 * organization's users (`useMspUsers`), minus anyone who is a client-org user.
 */
export const auditorCandidates = ({ users, organization, msp }) => {
  if (!msp?.applicable) return mspUsersOnly(users, organization);
  const clientEmails = new Set(
    excludeMspUsers(users, organization).map((user) => String(user?.email || "").toLowerCase())
  );
  return (msp.users || []).filter((user) => user?.email && !clientEmails.has(user.email.toLowerCase()));
};

/** Existing compliance controls route, keyed by the audit scope's framework name, run, and account. */
export const buildComplianceControlsPath = ({ frameworkName, runId, accountNumber, auditId }) => {
  const params = new URLSearchParams();
  params.set("compliance", frameworkName);
  if (runId) params.set("runId", runId);
  params.set("accountNumber", accountNumber);
  if (auditId) params.set("auditId", auditId);
  return `/compliance/controls?${params.toString()}`;
};
